import React, { useEffect, lazy } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { Alerts } from './components/Alerts';
import Home from './sections/Home';
import NotFound from './sections/NotFound';
import { actions as authActions } from './store/dux/auth-dux';
import { actions as environmentActions } from './store/dux/environment-dux';
import { routes } from './routes';
import { TfsRoute } from './routes/Route';

import FieldBoundaryManagement from './sections/field-boundary-management/FieldBoundaryManagement';
import GrowerManagement from './sections/grower-management/GrowerManagement';
import OperationManagement from './sections/grower-management/OperationManagement';
import OrganizationManagement from './sections/organization-management/OrganizationManagement';
import UserManagement from './sections/user-management/UserManagement';
import PotentialProductivity from './sections/potential-productivity/PotentialProductivity';
import Nrx from './sections/nrx/Nrx';
import SoyRXPage from './sections/soyrx/SoyRXPage';
import Lpk from './sections/lpk/Lpk';
import NProtectant from './sections/n-protectant/NProtectant';
import EarlySeasonFungicide from './sections/early-season-fungicide/EarlySeasonFungicide';
import GrowerYield from './sections/yield-display/GrowerYield';
import CornRXPage from './sections/cornrx/CornRXPage';
import FieldBoundaryUpload from './sections/field-boundary-upload/FieldBoundaryUpload';
import PlantingUpload from './sections/planting-upload/PlantingUpload';
import YieldUpload from './sections/yield-upload/YieldUpload';
import SoilCsvUpload from './sections/soil-pending-fields/SoilCsvUpload';
import PendingFields from './sections/soil-pending-fields/PendingFields';
import EoyReport from './sections/eoy/EoyReport';
import SoilReport from './sections/soil/SoilReport';
import FieldAgrilytics from './sections/field-agrilytics/FieldAgrilytics';
import AdminDashboard from './sections/admin/AdminDashboard';

const SoilSamplingInternalDashboard = lazy(
  () => import('./sections/soil-sampling/FieldSpecialistDashboard'),
);

const SoilSampleOrderPage = lazy(
  () => import('./sections/soil-sampling/OrderPage'),
);

const SoilSampleOrderAdminPage = lazy(
  () => import('./sections/soil-sampling/AdminPage'),
);

const SoilSampleOrderDashboardPage = lazy(
  () => import('./sections/soil-sampling/Dashboard'),
);

const StarterFertilizerPage = lazy(
  () => import('./sections/starter-fertilizer/StarterFertilizerPage'),
);

const Planting = lazy(() => import('./sections/planting/PlantingPage'));
const Deere = lazy(() => import('./sections/deere/Deere'));
const Signal = lazy(() => import('./sections/signal/Signal'));

const componentRegistry = {
  CornRXPage,
  Deere,
  EarlySeasonFungicide,
  EoyReport,
  FieldBoundaryManagement,
  FieldBoundaryUpload,
  GrowerManagement,
  GrowerYield,
  SoilSamplingInternalDashboard,
  Lpk,
  NProtectant,
  Nrx,
  OperationManagement,
  OrganizationManagement,
  PendingFields,
  Planting,
  PlantingUpload,
  PotentialProductivity,
  SoilCsvUpload,
  SoilReport,
  SoilSampleOrderAdminPage,
  SoilSampleOrderDashboardPage,
  SoilSampleOrderPage,
  SoyRXPage,
  StarterFertilizerPage,
  Signal,
  UserManagement,
  YieldUpload,
  AdminDashboard,
  FieldAgrilytics,
};

export default function Main({ user }) {
  const isAuthenticated = Boolean(user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(environmentActions.loadEnvironment());
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isAuthenticated) dispatch(authActions.login());
  }, [dispatch, isAuthenticated]);

  return (
    <>
      <Alerts />
      <Routes>
        <Route exact path="/" element={isAuthenticated ? <Home /> : null} />

        {routes.map(renderRoute)}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function renderRoute(routeConfig, key) {
  const { component, ...passThrough } = routeConfig;
  const Child = componentRegistry[component];

  return (
    <Route
      key={key}
      {...passThrough}
      element={
        <TfsRoute {...passThrough}>
          <Child />
        </TfsRoute>
      }
    />
  );
}
